(function initWebTranslateV2Bootstrap() {
  const TEXT_NODE = Node.TEXT_NODE;
  const ELEMENT_NODE = Node.ELEMENT_NODE;

  const DEFAULTS = {
    version: '2.1.10',
    apiEndpoint: '',
    apiKey: '',
    sourceLang: 'en',
    targetLang: 'en',
    switcher: '#language-selector',

    elementsToTranslate: '*:not(script, style, noscript, iframe, template)',
    elementsToTranslatePlaceholders: 'input[placeholder], textarea[placeholder], [placeholder]',
    elementsToTranslateValues: 'input[type="button"], input[type="submit"], input[type="reset"]',

    skipSelector: '#language-selector, #language-selector *, .no-translate, .no-translate *, [data-role="timer"], [data-role="timer"] *, [data-wt-ignore], [data-wt-ignore] *, [data-wt-bridge-ignore], [data-wt-bridge-ignore] *',

    caching: true,
    cacheTtlMinutes: 24 * 60,
    debug: false,

    // Событие от Vue-бриджа (webTranslateBridge.ts)
    eventName: 'wt:content-updated',

    // Событие выбора языка из Vue-селектора
    languageEventName: 'wt:language-changed',

    // Debounce для очереди "динамических" (MutationObserver + bridge)
    subtreeDebounceMs: 250,
    subtreeMaxWaitMs: 1500,

    minRequestIntervalMs: 500,
    requestDedupWindowMs: 2000,
    revalidateIntervalMs: 5 * 60 * 1000,

    // Защита от "шумных" узлов (таймеры, счётчики), которых не поймал skipSelector
    rateLimitWindowMs: 3000,
    rateLimitMaxTriggers: 120,
    rateLimitBlacklistMs: 30000,

    onTranslateComplete: function onTranslateComplete() {},
    onResetTranslationComplete: function onResetTranslationComplete() {},
  };

  function createWidget(userOptions = {}) {
    const settings = {...DEFAULTS, ...userOptions};

    const SELECTED_LANGUAGE_KEY = 'wt-selected-language';
    const MAX_ITEMS_PER_REQUEST = 100;

    const originalTextByNode = new WeakMap();
    const originalPlaceholderByNode = new WeakMap();
    const originalValueByNode = new WeakMap();
    const appliedTextByNode = new WeakMap();
    const appliedPlaceholderByNode = new WeakMap();
    const appliedValueByNode = new WeakMap();

    let currentLang = String(settings.targetLang || settings.sourceLang).toLowerCase();

    let subtreeTimer = null;
    let subtreeFirstQueuedAt = 0;
    const subtreeQueue = new Set();
    let subtreeRetryCount = 0;

    let initializedFromStorage = false;
    let lastRequestAt = 0;
    const recentSignatures = new Map();
    const lastServerSyncAtByLang = new Map();
    const translationsByLang = new Map();
    let translationRequestChain = Promise.resolve();

    // Guard от собственных мутаций (withMutationGuard)
    let isApplyingTranslations = false;

    // Circuit-breaker для "шумных" узлов, которые не покрыл skipSelector
    const rateLimitState = new WeakMap();
    let blacklistedRoots = new WeakMap();

    let isSwitchingLanguage = false;

    function log(...args) {
      if (settings.debug) {
        console.log('[WebTranslate v2]', ...args);
      }
    }

    function toArray(x) {
      return x ? Array.from(x) : [];
    }

    function normalizeText(text) {
      if (typeof text !== 'string') return '';
      return text.replace(/\u00A0/g, ' ').trim();
    }

    function normalizeLookupKey(text) {
      return normalizeText(text).toLocaleLowerCase();
    }

    function isSkippableElement(el) {
      return !(el instanceof Element) || el.matches(settings.skipSelector) || el.closest(settings.skipSelector) !== null;
    }

    const datePartPattern = /\b(?:\d{4}[-/.]\d{1,2}[-/.]\d{1,2}(?:[ T]\d{1,2}:\d{2}(?::\d{2})?)?|\d{1,2}[./]\d{1,2}[./]\d{2,4}|\d{1,2}\s+(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)(?:\s+\d{4})?|(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(?:\d{1,2}(?:,\s*\d{4})?|\d{4}))\b/giu;
    const timePartPattern = /\b\d{1,2}:\d{2}(?::\d{2})?\b/gu;
    const numberPartPattern = /(?<![\p{L}\d_])[+-]?(?:\d{1,3}(?:[ ,]\d{3})+|\d+)(?:[.,]\d+)?[KMB]?(?![\p{L}\d_])/giu;

    function maskDynamicParts(text) {
      const dateValues = [];
      const timeValues = [];
      const numberValues = [];
      const entities = [];

      let maskedText = text.replace(
        /&(?:#\d+|#x[0-9a-f]+|[a-z][a-z0-9]+);/giu,
        (value) => {
          const placeholder = `\uE000${'\uE001'.repeat(entities.length + 1)}`;
          entities.push({placeholder, value});
          return placeholder;
        },
      );

      maskedText = maskedText.replace(datePartPattern, (value) => {
        dateValues.push(value);
        return '{date}';
      });

      maskedText = maskedText.replace(timePartPattern, (value) => {
        timeValues.push(value);
        return '{time}';
      });

      maskedText = maskedText.replace(numberPartPattern, (value) => {
        numberValues.push(value);
        return '{number}';
      });

      entities.reverse().forEach(({placeholder, value}) => {
        maskedText = maskedText.replaceAll(placeholder, value);
      });

      return {
        text: normalizeText(maskedText),
        dateValues,
        timeValues,
        numberValues,
      };
    }

    function isDateOrTimeOnly(text) {
      const masked = maskDynamicParts(text).text.replace(/\{(?:date|time)\}/gu, '').replace(/[\s.,:;|/\\\-–—]+/gu, '');

      return masked === '';
    }

    function isNumericLikeNonTranslatable(text) {
      return /^[+-]?\s*(?:(?:[$€£¥₽₹₴₸₺₼₾₿]|(?:USD|EUR|GBP|JPY|CNY|RUB|INR|KRW|AUD|CAD|CHF|HKD|NZD|SGD|SEK|NOK|DKK|PLN|CZK|TRY|BRL|MXN|ZAR|AED|SAR|UAH|KZT|GEL|ILS|THB|VND|IDR|TWD))\s*)?(?:\d{1,3}(?:[ ,]\d{3})+|\d+)(?:[.,]\d+)?\s*%?(?:\s*(?:[$€£¥₽₹₴₸₺₼₾₿]|(?:USD|EUR|GBP|JPY|CNY|RUB|INR|KRW|AUD|CAD|CHF|HKD|NZD|SGD|SEK|NOK|DKK|PLN|CZK|TRY|BRL|MXN|ZAR|AED|SAR|UAH|KZT|GEL|ILS|THB|VND|IDR|TWD)))?$/iu.test(
          text);
    }

    function isStructuredIdentifierNonTranslatable(text) {
      return /^(?:[*xX•]{4}\s+){3}(?:[*xX•]{4}|\d{4})$/u.test(text)
        || /^[A-Z]{2,10}:\s*[A-Z0-9_-]*\d[A-Z0-9_-]{5,}$/u.test(text)
        || /^[A-Z]{2,10}[A-Z0-9_-]*\d[A-Z0-9_-]{5,}$/u.test(text)
        || /^\d+(?:[.,]\d+)?-\d+(?:[.,]\d+)?[KMB]?$/iu.test(text)
        || /^[A-Z]{2}$/u.test(text)
        || /^[+-]?\d+(?:[.,]\d+)?[KMB]$/iu.test(text);
    }

    function isEmailLikeNonTranslatable(text) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(text);
    }

    function isCssLikeNonTranslatable(text) {
      if (!text) return false;
      if (text.includes('@keyframes')) return true;
      const hasBlocks = text.includes('{') && text.includes('}');
      const hasCssSyntax = text.includes(':') && text.includes(';');
      if (hasBlocks && hasCssSyntax) return true;
      return /\b(?:position|z-index|transform|opacity|animation|background|font-family|border-radius|box-shadow)\s*:/iu.test(text);
    }

    function shouldTranslateText(text) {
      const t = normalizeText(text);
      if (!t || t.length < 2) return false;
      if (isDateOrTimeOnly(t)) return false;
      if (isNumericLikeNonTranslatable(t)) return false;
      if (isStructuredIdentifierNonTranslatable(t)) return false;
      if (/^:\d{1,2}$/u.test(t)) return false;
      if (isEmailLikeNonTranslatable(t)) return false;

      return !isCssLikeNonTranslatable(t);
    }

    function prepareTextForTranslation(text) {
      const normalized = normalizeText(text);
      if (!shouldTranslateText(normalized)) return null;

      const masked = maskDynamicParts(normalized);
      const meaningfulText = masked.text.replace(/\{(?:date|time|number)\}/gu, '').trim();

      if (!/\p{L}/u.test(meaningfulText)) return null;

      return masked;
    }

    function restoreDynamicParts(translated, prepared) {
      let value = translated;
      let dateIndex = 0;
      let timeIndex = 0;
      let numberIndex = 0;

      value = value.replace(/\{date\}/giu, () => prepared.dateValues[dateIndex++] ?? '{date}');
      value = value.replace(/\{time\}/giu, () => prepared.timeValues[timeIndex++] ?? '{time}');
      value = value.replace(/\{number\}/giu, () => prepared.numberValues[numberIndex++] ?? '{number}');

      return value;
    }

    function macroCounts(text) {
      const counts = {date: 0, time: 0, number: 0};
      const matches = String(text || '').match(/\{(date|time|number)\}/gu) || [];

      matches.forEach((macro) => {
        counts[macro.slice(1, -1).toLowerCase()]++;
      });

      return counts;
    }

    function hasUnknownMacros(text) {
      const macros = String(text || '').match(/\{[^{}]*\}/gu) || [];

      return macros.some((macro) => !/^\{(?:date|time|number)\}$/u.test(macro));
    }

    function hasValidMacros(original, translated) {
      return !hasUnknownMacros(translated)
        && JSON.stringify(macroCounts(original)) === JSON.stringify(macroCounts(translated));
    }

    // ---------------- localStorage ----------------
    // Один ключ на язык: wt-translation-<lang> = { [pathname]: { translated, timestamp, expiresAt } }

    function storageKeyForLang(lang) {
      return `wt-translation-${lang}`;
    }

    function readLangStorage(lang) {
      try {
        const raw = window.localStorage.getItem(storageKeyForLang(lang));
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {};
      }
    }

    function writeLangStorage(lang, data) {
      try {
        window.localStorage.setItem(storageKeyForLang(lang), JSON.stringify(data));
      } catch {
        // ignore
      }
    }

    function readCache(lang) {
      if (!settings.caching) return null;

      const store = readLangStorage(lang);
      const path = window.location.pathname;
      const entry = store[path];
      if (!entry) return null;

      if (Date.now() > entry.expiresAt) {
        delete store[path];
        writeLangStorage(lang, store);
        return null;
      }

      return entry.translated ? {translated: entry.translated} : null;
    }

    function getAvailableTranslations(lang) {
      const cached = readCache(lang);
      const memoryTranslations = translationsByLang.get(lang);

      if (!cached?.translated && !memoryTranslations) return null;

      return {
        translated: {
          ...(memoryTranslations || {}),
          ...(cached?.translated || {}),
        },
      };
    }

    function writeCache(lang, data) {
      if (!settings.caching || !data?.translated) return;

      const store = readLangStorage(lang);
      const path = window.location.pathname;

      store[path] = {
        translated: data.translated,
        timestamp: Date.now(),
        expiresAt: Date.now() + settings.cacheTtlMinutes * 60 * 1000,
      };

      writeLangStorage(lang, store);
    }

    function getCachedOriginalTextSet(cached) {
      const set = new Set();
      const translated = cached?.translated || {};
      Object.values(translated).forEach((row) => {
        if (row?.originalText) set.add(String(row.originalText));
      });
      return set;
    }

    function hasAllTextsInCache(texts, cached) {
      if (!settings.caching || !cached?.translated) return false;
      const cachedSet = getCachedOriginalTextSet(cached);
      return texts.every((text) => cachedSet.has(text));
    }

    function shouldRevalidate(lang) {
      const lastSync = lastServerSyncAtByLang.get(lang) || 0;
      return Date.now() - lastSync >= settings.revalidateIntervalMs;
    }

    // ---------------- Сбор / применение / сброс перевода ----------------

    function queryTargets(root, selector) {
      if (root === document.body) {
        return toArray(document.querySelectorAll(selector));
      }
      const self = root instanceof Element && root.matches(selector) ? [root] : [];
      return self.concat(toArray(root.querySelectorAll(selector)));
    }

    function getOriginalValue(node, currentValue, originalMap, appliedMap) {
      const hasOriginal = originalMap.has(node);
      const hasApplied = appliedMap.has(node);

      // Vue can reuse a text node/element and replace its source value. If the
      // current value is no longer the value applied by the translator, this
      // is a new source string and the old mapping must not be reused.
      if (!hasOriginal || (hasApplied && currentValue !== appliedMap.get(node))) {
        originalMap.set(node, currentValue);
        appliedMap.delete(node);
      }

      return originalMap.get(node) ?? currentValue;
    }

    function collectFromRoot(root) {
      if (!(root instanceof Element) && root !== document.body) {
        return {texts: []};
      }

      const texts = [];
      const seen = new Set();

      queryTargets(root, settings.elementsToTranslate).forEach((el) => {
        if (isSkippableElement(el)) return;

        toArray(el.childNodes).forEach((node) => {
          if (node.nodeType !== TEXT_NODE) return;

          const raw = node.textContent || '';
          // ВАЖНО: если узел уже переводился ранее, используем сохранённый
          // ОРИГИНАЛ (английский), а не текущий textContent. Иначе при
          // повторном сканировании уже переведённого поддерева (например,
          // родительский Vue-компонент перерендерился из-за не связанного
          // с переводом изменения состояния — открытие дропдауна и т.п.)
          // мы отправим на сервер уже ПЕРЕВЕДЁННЫЙ текст как "оригинал",
          // получим несовпадение с кэшем/API и откатим перевод на английский.
          const original = getOriginalValue(
              node,
              raw,
              originalTextByNode,
              appliedTextByNode,
          );
          const text = normalizeText(original);
          const prepared = prepareTextForTranslation(text);
          if (!prepared) return;

          if (!seen.has(prepared.text)) {
            seen.add(prepared.text);
            texts.push(prepared.text);
          }
        });
      });

      queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
        if (isSkippableElement(el)) return;

        const placeholder = el.getAttribute('placeholder');
        const original = getOriginalValue(
            el,
            placeholder || '',
            originalPlaceholderByNode,
            appliedPlaceholderByNode,
        );
        const text = normalizeText(original);
        const prepared = prepareTextForTranslation(text);
        if (!prepared) return;

        if (!seen.has(prepared.text)) {
          seen.add(prepared.text);
          texts.push(prepared.text);
        }
      });

      queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
        if (!(el instanceof HTMLInputElement) || isSkippableElement(el)) return;

        const value = el.value || '';
        const original = getOriginalValue(
            el,
            value,
            originalValueByNode,
            appliedValueByNode,
        );
        const text = normalizeText(original);
        const prepared = prepareTextForTranslation(text);
        if (!prepared) return;

        if (!seen.has(prepared.text)) {
          seen.add(prepared.text);
          texts.push(prepared.text);
        }
      });

      return {texts};
    }

    function buildTranslationMap(translatedMap) {
      const map = new Map();
      Object.values(translatedMap || {}).forEach((row) => {
        if (row?.originalText
          && row?.translatedText
          && row.originalText !== row.translatedText
          && hasValidMacros(row.originalText, row.translatedText)) {
          map.set(normalizeLookupKey(row.originalText), row.translatedText);
        }
      });
      return map;
    }

    function applyCachedTranslationsToRoot(root) {
      if (currentLang === settings.sourceLang) return;
      if (!(root instanceof Element) && root !== document.body) return;

      const cached = getAvailableTranslations(currentLang);
      if (!cached?.translated) return;

      applyTranslations(root, cached.translated);
    }

    function applyTranslatedValue(original, translationMap) {
      const prepared = prepareTextForTranslation(original);
      if (!prepared) return null;

      const translated = translationMap.get(normalizeLookupKey(prepared.text));

      if (!translated || !hasValidMacros(prepared.text, translated)) return null;

      const normalized = normalizeText(original);
      const restored = restoreDynamicParts(translated, prepared);
      if (original.includes(normalized)) {
        return original.replace(normalized, restored);
      }
      return restored;
    }

    function withMutationGuard(fn) {
      isApplyingTranslations = true;
      try {
        fn();
      } finally {
        // MutationObserver callbacks are delivered at the same microtask
        // checkpoint as Vue updates. Keep the guard until the next task so
        // the observer does not process mutations made by applyTranslations.
        window.setTimeout(() => {
          isApplyingTranslations = false;
        }, 0);
      }
    }

    function applyTranslations(root, translatedMap) {
      const translationMap = buildTranslationMap(translatedMap);
      if (!translationMap.size) return;

      withMutationGuard(() => {
        queryTargets(root, settings.elementsToTranslate).forEach((el) => {
          if (isSkippableElement(el)) return;

          toArray(el.childNodes).forEach((node) => {
            if (node.nodeType !== TEXT_NODE) return;

            const original = getOriginalValue(
                node,
                node.textContent ?? '',
                originalTextByNode,
                appliedTextByNode,
            );

            const next = applyTranslatedValue(original, translationMap);
            if (typeof next === 'string' && next !== node.textContent) {
              node.textContent = next;
              appliedTextByNode.set(node, next);
            } else if (node.textContent === original) {
              appliedTextByNode.delete(node);
            }
          });
        });

        queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
          if (isSkippableElement(el)) return;

          const original = getOriginalValue(
              el,
              el.getAttribute('placeholder') ?? '',
              originalPlaceholderByNode,
              appliedPlaceholderByNode,
          );

          const next = applyTranslatedValue(original, translationMap);
          if (typeof next === 'string' && next !== (el.getAttribute('placeholder') ?? '')) {
            el.setAttribute('placeholder', next);
            appliedPlaceholderByNode.set(el, next);
          } else if (el.getAttribute('placeholder') === original) {
            appliedPlaceholderByNode.delete(el);
          }
        });

        queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
          if (!(el instanceof HTMLInputElement) || isSkippableElement(el)) return;

          const original = getOriginalValue(
              el,
              el.value ?? '',
              originalValueByNode,
              appliedValueByNode,
          );

          const next = applyTranslatedValue(original, translationMap);
          if (typeof next === 'string' && next !== el.value) {
            el.value = next;
            appliedValueByNode.set(el, next);
          } else if (el.value === original) {
            appliedValueByNode.delete(el);
          }
        });
      });
    }

    function resetRoot(root = document.body) {
      withMutationGuard(() => {
        queryTargets(root, settings.elementsToTranslate).forEach((el) => {
          toArray(el.childNodes).forEach((node) => {
            if (node.nodeType !== TEXT_NODE) return;
            const original = originalTextByNode.get(node);
            if (typeof original === 'string' && node.textContent !== original) {
              node.textContent = original;
            }
            originalTextByNode.delete(node);
            appliedTextByNode.delete(node);
          });
        });

        queryTargets(root, settings.elementsToTranslatePlaceholders).forEach((el) => {
          const original = originalPlaceholderByNode.get(el);
          if (typeof original === 'string') el.setAttribute('placeholder', original);
          originalPlaceholderByNode.delete(el);
          appliedPlaceholderByNode.delete(el);
        });

        queryTargets(root, settings.elementsToTranslateValues).forEach((el) => {
          if (!(el instanceof HTMLInputElement)) return;
          const original = originalValueByNode.get(el);
          if (typeof original === 'string') el.value = original;
          originalValueByNode.delete(el);
          appliedValueByNode.delete(el);
        });
      });

      settings.onResetTranslationComplete(true);
    }

    async function fetchTranslationsBatched(texts, targetLang) {
      if (!texts.length) return {translated: {}};

      const normalizedTexts = Array.from(new Set(texts)).sort();
      const signature = `${targetLang}|${normalizedTexts.join('||')}`;
      const textChunks = [];

      for (let index = 0; index < normalizedTexts.length; index += MAX_ITEMS_PER_REQUEST) {
        textChunks.push(normalizedTexts.slice(index, index + MAX_ITEMS_PER_REQUEST));
      }

      const runRequest = async () => {
        const now = Date.now();

        for (const [key, ts] of recentSignatures.entries()) {
          if (now - ts > settings.requestDedupWindowMs) {
            recentSignatures.delete(key);
          }
        }

        // Re-read the cache after waiting for the previous request. This is
        // important for the initial page request and a Vue subtree request
        // that are created at the same time.
        const cached = getAvailableTranslations(targetLang);
        const forceRevalidate = shouldRevalidate(targetLang);

        if (hasAllTextsInCache(normalizedTexts, cached) && !forceRevalidate) {
          return {translated: cached?.translated || {}};
        }

        if (recentSignatures.has(signature) && cached?.translated) {
          return {translated: cached.translated};
        }

        let mergedTranslated = {...(cached?.translated || {})};

        for (const chunk of textChunks) {
          const chunkCached = getAvailableTranslations(targetLang);

          if (hasAllTextsInCache(chunk, chunkCached) && !forceRevalidate) {
            mergedTranslated = {
              ...mergedTranslated,
              ...(chunkCached?.translated || {}),
            };
            continue;
          }

          const requestNow = Date.now();
          if (requestNow - lastRequestAt < settings.minRequestIntervalMs) {
            const waitMs = settings.minRequestIntervalMs - (requestNow - lastRequestAt);
            await new Promise((resolve) => window.setTimeout(resolve, waitMs));
          }

          const cacheVersions = {};
          if (chunkCached?.translated) {
            Object.values(chunkCached.translated).forEach((row) => {
              if (row?.originalText && Number.isInteger(row?.cache_version)) {
                cacheVersions[row.originalText] = row.cache_version;
              }
            });
          }

          const items = chunk.map((text, index) => ({id: `t_${index}`, text}));
          const headers = {'Content-Type': 'application/json'};
          if (settings.apiKey) {
            headers.Authorization = `Bearer ${settings.apiKey}`;
          }

          const response = await fetch(settings.apiEndpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              sourceLang: settings.sourceLang,
              targetLang,
              items,
              cache_versions: cacheVersions,
              caching: settings.caching,
              page_url: window.location.href,
            }),
          });

          lastRequestAt = Date.now();

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const json = await response.json();
          const translated = json?.data?.translated || {};
          mergedTranslated = {
            ...(getAvailableTranslations(targetLang)?.translated || {}),
            ...mergedTranslated,
            ...translated,
          };

          // Save every successful chunk so a later temporary failure does not discard the translations already received.
          writeCache(targetLang, {translated: mergedTranslated});
          translationsByLang.set(targetLang, mergedTranslated);
          lastServerSyncAtByLang.set(targetLang, Date.now());
        }

        recentSignatures.set(signature, Date.now());
        translationsByLang.set(targetLang, mergedTranslated);
        return {translated: mergedTranslated};
      };

      // Serialize all translation requests. Without this, a fast subtree
      // request could finish before the initial page request and cause the
      // latter response to be discarded, leaving random parts untranslated.
      const request = translationRequestChain.then(runRequest, runRequest);
      translationRequestChain = request.catch(() => undefined);

      return request;
    }

    async function translateSubtree(root, targetLang = currentLang) {
      if (!(root instanceof Element) && root !== document.body) return;
      if (targetLang === settings.sourceLang || currentLang !== targetLang) return;

      const {texts} = collectFromRoot(root);
      if (!texts.length) return;

      try {
        const data = await fetchTranslationsBatched(texts, targetLang);
        if (currentLang === targetLang) {
          applyTranslations(root, data.translated);
        }
      } catch (e) {
        log('translateSubtree error', e);
      }
    }

    async function resetPage() {
      resetRoot(document.body);
    }

    async function translatePage() {
      const targetLang = currentLang;

      try {
        await resetPage();
        await translateSubtree(document.body, targetLang);
        settings.onTranslateComplete(currentLang === targetLang);
      } catch (e) {
        log('translatePage error', e);
        settings.onTranslateComplete(false);
      }
    }

    async function setLanguage(lang) {
      const normalized = String(lang || settings.sourceLang).toLowerCase();
      currentLang = normalized;

      try {
        window.localStorage.setItem(SELECTED_LANGUAGE_KEY, normalized);
      } catch {
        // ignore
      }

      // Сбрасываем "шумовые" ограничители и очередь - требуется полный перевод страницы.
      isSwitchingLanguage = true;
      blacklistedRoots = new WeakMap();
      subtreeQueue.clear();
      subtreeRetryCount = 0;
      if (subtreeTimer) {
        window.clearTimeout(subtreeTimer);
        subtreeTimer = null;
      }
      subtreeFirstQueuedAt = 0;

      try {
        if (normalized === settings.sourceLang) {
          await resetPage();
        } else {
          await translatePage();
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          await translateSubtree(document.body);
        }
      } finally {
        isSwitchingLanguage = false;
      }
    }

    async function translateText(text, targetLang) {
      const t = normalizeText(text);
      const lang = String(targetLang || currentLang).toLowerCase();
      const prepared = prepareTextForTranslation(t);
      if (!prepared || lang === settings.sourceLang) return text;

      try {
        const data = await fetchTranslationsBatched([prepared.text], lang);
        const rows = Object.values(data?.translated || {});
        const row = rows.find((r) => normalizeLookupKey(r?.originalText || '') === normalizeLookupKey(prepared.text));
        return row?.translatedText && hasValidMacros(prepared.text, row.translatedText)
            ? restoreDynamicParts(row.translatedText, prepared)
            : text;
      } catch (e) {
        log('translateText error', e);
        return text;
      }
    }

    // ---------------- Очередь динамических ----------------
    // Используется и Vue-бриджем (wt:content-updated), и MutationObserver'ом.

    function isDescendantOfQueued(el) {
      for (const queuedRoot of subtreeQueue) {
        if (queuedRoot !== el && queuedRoot.contains(el)) return true;
      }
      return false;
    }

    function isCoveredByQueuedRoot(el) {
      return subtreeQueue.has(el) || isDescendantOfQueued(el);
    }

    function addRootToQueue(root) {
      if (!(root instanceof Element) || !root.isConnected) return;
      if (isSkippableElement(root)) return;
      if (isDescendantOfQueued(root)) return;

      // Удаляем из очереди узлы, которые теперь являются потомками нового root
      for (const queuedRoot of Array.from(subtreeQueue)) {
        if (queuedRoot !== root && root.contains(queuedRoot)) {
          subtreeQueue.delete(queuedRoot);
        }
      }

      subtreeQueue.add(root);
      if (!subtreeFirstQueuedAt) {
        subtreeFirstQueuedAt = Date.now();
      }
    }

    async function flushSubtreeQueue() {
      subtreeTimer = null;
      subtreeFirstQueuedAt = 0;

      if (currentLang === settings.sourceLang) {
        subtreeQueue.clear();
        subtreeRetryCount = 0;
        return;
      }

      const roots = Array.from(subtreeQueue);
      subtreeQueue.clear();
      if (!roots.length) {
        subtreeRetryCount = 0;
        return;
      }

      const allTextsSet = new Set();
      const rootToTexts = new Map();

      roots.forEach((root) => {
        if (!root.isConnected) return;
        const {texts} = collectFromRoot(root);
        if (!texts.length) return;
        rootToTexts.set(root, texts);
        texts.forEach((t) => allTextsSet.add(t));
      });

      const allTexts = Array.from(allTextsSet);
      if (!allTexts.length) {
        subtreeRetryCount = 0;
        return;
      }

      const targetLang = currentLang;
      try {
        const data = await fetchTranslationsBatched(allTexts, targetLang);

        rootToTexts.forEach((_texts, root) => {
          if (root.isConnected && currentLang === targetLang) {
            applyTranslations(root, data.translated);
          }
        });
        subtreeRetryCount = 0;
      } catch (e) {
        log('flushSubtreeQueue error', e);

        // A temporary API/network error must not permanently lose a Vue
        // subtree from the queue. Retry a few times with a growing delay.
        if (currentLang === targetLang && subtreeRetryCount < 3) {
          subtreeRetryCount += 1;
          roots.forEach((root) => addRootToQueue(root));
          subtreeFirstQueuedAt = Date.now();
          subtreeTimer = window.setTimeout(
              flushSubtreeQueue,
              1000 * subtreeRetryCount,
          );
        }
      }
    }

    function scheduleSubtreeTranslate(root) {
      if (currentLang === settings.sourceLang) return;
      if (!(root instanceof Element)) return;

      addRootToQueue(root);

      if (subtreeTimer) return;

      const elapsedSinceFirst = subtreeFirstQueuedAt ? Date.now() - subtreeFirstQueuedAt : 0;
      const delay = elapsedSinceFirst >= settings.subtreeMaxWaitMs ? 0 : settings.subtreeDebounceMs;

      subtreeTimer = window.setTimeout(flushSubtreeQueue, delay);
    }

    // ---------------- Circuit breaker для "шумных" узлов ----------------
    function isRootTemporarilyBlacklisted(root) {
      const until = blacklistedRoots.get(root);
      if (!until) return false;
      if (Date.now() > until) {
        blacklistedRoots.delete(root);
        return false;
      }
      return true;
    }

    function registerRootTrigger(root) {
      const now = Date.now();
      const state = rateLimitState.get(root) || {count: 0, windowStart: now};

      if (state.lastTriggerAt && now - state.lastTriggerAt < settings.subtreeDebounceMs) {
        return true;
      }

      if (now - state.windowStart > settings.rateLimitWindowMs) {
        state.count = 0;
        state.windowStart = now;
      }

      state.count += 1;
      state.lastTriggerAt = now;
      rateLimitState.set(root, state);

      if (state.count > settings.rateLimitMaxTriggers) {
        blacklistedRoots.set(root, now + settings.rateLimitBlacklistMs);
        log('Root temporarily excluded from translation (too many mutations):', root);
        return false;
      }

      return true;
    }

    // ---------------- MutationObserver ----------------
    function elementFromMutationTarget(node) {
      if (node instanceof Element) return node;
      if (node && node.parentElement) return node.parentElement;
      return null;
    }

    function handleMutations(mutations) {
      if (isApplyingTranslations) return;
      if (currentLang === settings.sourceLang) return;

      mutations.forEach((mutation) => {
        const candidates = [];

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== ELEMENT_NODE && node.nodeType !== TEXT_NODE) return;
            const el = elementFromMutationTarget(node);
            if (el) candidates.push(el);
          });
        } else if (mutation.type === 'characterData') {
          const el = elementFromMutationTarget(mutation.target);
          if (el) candidates.push(el);
        }

        candidates.forEach((el) => {
          if (isSkippableElement(el)) return;

          // Сначала мгновенно переводим то, что уже есть в кэше
          applyCachedTranslationsToRoot(el);

          // Очередь сама объединяет MutationObserver и Vue bridge события.
          // Поэтому одно и то же обновление не должно дважды увеличивать
          // счётчик circuit-breaker.
          if (!isSwitchingLanguage && !isCoveredByQueuedRoot(el)) {
            if (isRootTemporarilyBlacklisted(el)) return;
            if (!registerRootTrigger(el)) return;
          }

          scheduleSubtreeTranslate(el);
        });
      });
    }

    let mutationObserver = null;

    function initMutationObserver() {
      mutationObserver = new MutationObserver(handleMutations);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // ---------------- Bridge (Vue) ----------------
    function bindBridgeEvent() {
      window.addEventListener(settings.eventName, (event) => {
        if (currentLang === settings.sourceLang) return;

        const detail = event?.detail || {};
        const roots = Array.isArray(detail.roots)
            ? detail.roots
            : (detail.root instanceof Element ? [detail.root] : []);

        roots.forEach((root) => {
          if (!(root instanceof Element)) return;
          if (isSkippableElement(root)) return;

          // Мгновенно применяем известные переводы (в т.ч. к пересозданным узлам VButton), затем — обычная очередь под circuit-breaker.
          applyCachedTranslationsToRoot(root);

          if (!isSwitchingLanguage && !isCoveredByQueuedRoot(root)) {
            if (isRootTemporarilyBlacklisted(root)) return;
            if (!registerRootTrigger(root)) return;
          }

          scheduleSubtreeTranslate(root);
        });
      });
    }

    // ---------------- Switcher / cross-tab sync ----------------
    function bindSwitcher() {
      const getSwitcher = () => document.querySelector(settings.switcher);

      // Native select остается compatibility-слоем для приватных страниц
      // Основной публичный селектор теперь сообщает о выборе через window event
      document.addEventListener('change', (event) => {
        const switcher = getSwitcher();
        const target = event?.target;
        if (!switcher || target !== switcher) return;

        const value = String(target?.value || settings.sourceLang).toLowerCase();
        setLanguage(value).then(() => {});
      });

      window.addEventListener(settings.languageEventName, (event) => {
        const value = String(event?.detail?.language || settings.sourceLang).toLowerCase();
        const switcher = getSwitcher();
        if (switcher && 'value' in switcher) {
          switcher.value = value;
        }
        setLanguage(value).then(() => {});
      });

      try {
        const fromStorage = window.localStorage.getItem(SELECTED_LANGUAGE_KEY);
        if (fromStorage) {
          const switcher = getSwitcher();
          if (switcher && 'value' in switcher) {
            switcher.value = fromStorage;
          }
          initializedFromStorage = true;
          setLanguage(fromStorage).then(() => {});
        }
      } catch {
        // ignore
      }
    }

    function bindStorageSync() {
      window.addEventListener('storage', (event) => {
        if (event.key !== SELECTED_LANGUAGE_KEY) return;

        const lang = String(event.newValue || '').toLowerCase();
        if (!lang) return;

        const switcher = document.querySelector(settings.switcher);
        if (switcher && 'value' in switcher) switcher.value = lang;

        setLanguage(lang).then(() => {});
      });
    }

    function init() {
      log('init', settings.version);

      // Observer запускаем всегда: внутри он сразу выходит
      initMutationObserver();
      bindBridgeEvent();
      bindSwitcher();
      bindStorageSync();

      const runInitialTranslate = () => {
        if (!initializedFromStorage && currentLang !== settings.sourceLang) {
          translatePage().then(() => {});
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runInitialTranslate);
      } else {
        runInitialTranslate();
      }
    }

    init();

    return {
      setLanguage,
      translatePage,
      translateSubtree,
      resetPage,
      translateText,
      translateElement: scheduleSubtreeTranslate,
    };
  }

  const options = typeof window.wTranslateOptionsV2 === 'object'
      ? window.wTranslateOptionsV2
      : {};

  window.wTranslateV2 = createWidget(options);
})();
